/**
 * @cione/api-client —— 与网关通信的唯一入口。
 *
 * 网关是唯一的门(契约 §4)，对外只有 5 条路由；这一层把契约里那些
 * 「错了不会当场崩、只会静默走错分支」的规则收拢到一处实现：
 *
 *   - HTTP 层：`Result` 壳拆包、按 `code` 分支(不按状态码) ...... http.ts
 *   - 单飞刷新：并发撞到过期只刷一次，其余排队 ................. refresh.ts
 *   - `x-trace-id`：从**响应头**读 ............................. http.ts
 *   - SSE 读流：fetch + ReadableStream + eventsource-parser ..... sse.ts
 *   - cancel：以流上收到 `done` 为准再切「已停止」 .............. agent.ts
 */
import { createAgentApi, type AgentApi } from './agent'
import { createAuthApi, type AuthApi } from './auth'
import { createConversationsApi, type ConversationsApi } from './conversations'
import { createWebLocksGuard, type WithLock } from './cross-tab-lock'
import { createHttpClient, type HttpClient, type RetryPolicy } from './http'
import { createOrdersApi, type OrdersApi } from './orders'
import { createRefreshRunner, type AuthLostReason, type RefreshRunner } from './refresh'
import { createTravelersApi, type TravelersApi } from './travelers'
import { createMemoryTokenStore, type TokenStore } from './token-store'

export * from './errors'
export * from './cross-tab-lock'
export * from './token-store'
export * from './single-flight'
export * from './http'
export * from './refresh'
export * from './sse'
export * from './auth'
export * from './agent'
export * from './travelers'
export * from './orders'
export * from './conversations'

export interface ApiClientOptions {
  baseUrl: string
  tokenStore?: TokenStore | undefined
  fetch?: typeof globalThis.fetch | undefined
  timeoutMs?: number | undefined
  retry?: Partial<RetryPolicy> | undefined
  /** ⚠ 令牌彻底失效。整个刷新周期只触发一次 —— 登录框据此只弹一次。 */
  onAuthLost?: ((reason: AuthLostReason) => void) | undefined
  sleep?: ((ms: number) => Promise<void>) | undefined
  random?: (() => number) | undefined
  now?: (() => number) | undefined
  /**
   * 跨标签页刷新互斥(W1-3)。默认在浏览器里接 Web Locks，拿不到 API 自动降级。
   * ⚠ 测试里传 `withoutCrossTabLock` 可以钉死行为，不受运行环境影响。
   */
  withLock?: WithLock | undefined
}

export interface ApiClient {
  http: HttpClient
  auth: AuthApi
  agent: AgentApi
  travelers: TravelersApi
  orders: OrdersApi
  conversations: ConversationsApi
  tokenStore: TokenStore
  refresh: RefreshRunner
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const tokenStore = options.tokenStore ?? createMemoryTokenStore()
  const now = options.now ?? Date.now

  // 三者互相依赖(http 要刷新、刷新要 A5、A5 要 http)，用一个后绑的引用打破环
  let runner: RefreshRunner | null = null

  const http = createHttpClient({
    baseUrl: options.baseUrl,
    tokenStore,
    ...(options.fetch ? { fetch: options.fetch } : {}),
    ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
    ...(options.retry ? { retry: options.retry } : {}),
    ...(options.sleep ? { sleep: options.sleep } : {}),
    ...(options.random ? { random: options.random } : {}),
    onUnauthorized: (error, tokenUsed) =>
      runner ? runner.handleUnauthorized(error, tokenUsed) : Promise.resolve(false),
  })

  const auth = createAuthApi(http, now)

  runner = createRefreshRunner({
    tokenStore,
    // 包一层而不是直接传方法引用：直接传会把 this 丢掉(lint 也会拦)
    callRefresh: (refreshToken) => auth.callRefresh(refreshToken),
    ...(options.onAuthLost ? { onAuthLost: options.onAuthLost } : {}),
    // ⚠ 默认就接上：多标签是 Web 上的常态，忘了传等于这道保护不存在。
    //   jsdom 里没有 navigator.locks，会自动降级成不协调，单测不受影响。
    withLock: options.withLock ?? createWebLocksGuard(),
    now,
  })

  return {
    http,
    auth,
    agent: createAgentApi(http),
    travelers: createTravelersApi(http),
    orders: createOrdersApi(http),
    conversations: createConversationsApi(http),
    tokenStore,
    refresh: runner,
  }
}
